import React from 'react';
import { Battery, Wifi, Thermometer, Gauge, Navigation, Settings } from 'lucide-react';
import { Device } from '../lib/supabase';

interface DeviceStatsProps {
  device: Device | null;
  className?: string;
}

export default function DeviceStats({ device, className = '' }: DeviceStatsProps) {
  if (!device) {
    return (
      <div className={`bg-gray-50 rounded-xl p-4 ${className}`}>
        <p className="text-sm text-gray-500 text-center">Select a device to view stats</p>
      </div>
    );
  }

  const stats = [
    {
      icon: Battery,
      label: 'Battery',
      value: device.battery_percentage ? `${device.battery_percentage}%` : 'N/A',
      color: device.battery_percentage && device.battery_percentage > 20 ? 'text-green-600' : 'text-red-600'
    },
    {
      icon: Wifi,
      label: 'Signal',
      value: device.cell_signal_strength ? `${device.cell_signal_strength}/5` : 'N/A',
      color: 'text-blue-600'
    },
    {
      icon: Gauge,
      label: 'Speed',
      value: device.last_speed ? `${device.last_speed} km/h` : '0 km/h',
      color: 'text-purple-600'
    },
    {
      icon: Navigation,
      label: 'Heading',
      value: device.last_heading ? `${device.last_heading}°` : 'N/A',
      color: 'text-indigo-600'
    },
    {
      icon: Thermometer,
      label: 'Temperature',
      value: device.last_temperature ? `${device.last_temperature}°C` : 'N/A',
      color: 'text-orange-600'
    }
  ];

  // Add firmware version if available
  if (device.firmware_version) {
    stats.push({
      icon: Settings,
      label: 'Firmware',
      value: device.firmware_version.includes('V') 
        ? device.firmware_version.split('V')[1].split(':')[0] 
        : device.firmware_version.slice(-6),
      color: 'text-gray-600'
    });
  }

  return (
    <div className={`bg-white rounded-xl p-4 ${className}`}>
      <h3 className="text-sm font-medium text-gray-700 mb-3">Device Stats</h3>
      <div className="grid grid-cols-2 gap-3">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="flex items-center space-x-2">
              <Icon className={`w-4 h-4 ${stat.color}`} />
              <div>
                <p className="text-xs text-gray-500">{stat.label}</p>
                <p className="text-sm font-medium text-gray-900">{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}